'use client';

import { FC, useState, useEffect, useRef } from 'react';
import { Content, asText, isFilled } from '@prismicio/client';
import type { ImageField } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';

/**
 * Props for `FeatureHighlightsWithMedia`.
 */
export type FeatureHighlightsWithMediaProps =
  SliceComponentProps<Content.FeatureHighlightsWithMediaSlice>;

/**
 * Component for "FeatureHighlightsWithMedia" Slices.
 */
type AppMediaItem = { appview: ImageField<never> };
type DefaultPrimary = Extract<
  Content.FeatureHighlightsWithMediaSlice,
  { variation: 'default' }
>['primary'];
type SimplifiedPrimary = Extract<
  Content.FeatureHighlightsWithMediaSlice,
  { variation: 'simplified' }
>['primary'];
type InterconnectedStepsPrimary = Extract<
  Content.FeatureHighlightsWithMediaSlice,
  { variation: 'interconnectedSteps' }
>['primary'];
type Simplified2ColumnsPrimary = Extract<
  Content.FeatureHighlightsWithMediaSlice,
  { variation: 'simplified2Columns' }
>['primary'];
type DoubleCardStepsPrimary = Extract<
  Content.FeatureHighlightsWithMediaSlice,
  { variation: 'doubleCardSteps' }
>['primary'];
type DefaultFeatureItem = NonNullable<DefaultPrimary['features']>[number];
type SimplifiedFeatureItem = NonNullable<SimplifiedPrimary['features']>[number];
type InterconnectedStepsFeatureItem = NonNullable<
  InterconnectedStepsPrimary['features']
>[number];
type Simplified2ColumnsFeatureItem = NonNullable<
  Simplified2ColumnsPrimary['features']
>[number];
type DoubleCardStepsFeatureItem = NonNullable<
  DoubleCardStepsPrimary['features']
>[number];

const FeatureHighlightsWithMedia: FC<FeatureHighlightsWithMediaProps> = ({
  slice,
}) => {
  const { section_heading } = slice.primary;
  // appmedia only exists on default variation; access defensively
  const appmedia =
    'appmedia' in slice.primary
      ? (slice.primary as { appmedia?: AppMediaItem[] }).appmedia
      : undefined;
  const isSimplified = slice.variation === 'simplified';
  const isInterconnectedSteps = slice.variation === 'interconnectedSteps';
  const isSimplified2Columns = slice.variation === 'simplified2Columns';
  const isDoubleCardSteps = slice.variation === 'doubleCardSteps';

  // Filter out empty images
  const validImages: AppMediaItem[] = Array.isArray(appmedia)
    ? appmedia.filter((media): media is AppMediaItem =>
        Boolean(media && isFilled.image(media.appview)),
      )
    : [];
  const hasImages = validImages.length > 0;

  // State for current image index
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Animation refs for interconnected steps
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!isInterconnectedSteps && !isDoubleCardSteps) return;

    const observers: IntersectionObserver[] = [];

    cardsRef.current.forEach((card, index) => {
      if (!card) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
              }, index * 100);
            }
          });
        },
        { threshold: 0.1 },
      );

      observer.observe(card);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [isInterconnectedSteps, isDoubleCardSteps]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + validImages.length) % validImages.length,
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="text-white font-inter py-16"
      style={{
        background: isSimplified
          ? 'linear-gradient(180deg, #0F423A 0%, #0E1010 100%)'
          : 'linear-gradient(180deg, #0F3932 58.58%, #0E1110 100%)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-10 lg:px-[174px]">
        {isSimplified ? (
          // Simplified variation: staircase feature cards only
          <div>
            {section_heading && (
              <h2 className="text-white font-inter font-medium text-[40px] leading-[120%] mb-10">
                <HighlightedText text={asText(section_heading) || ''} />
              </h2>
            )}
            {(slice.primary as SimplifiedPrimary).features &&
              (slice.primary as SimplifiedPrimary).features!.length > 0 &&
              (() => {
                const features =
                  (slice.primary as SimplifiedPrimary).features ?? [];
                const filtered = features.filter(
                  (f) => (f as SimplifiedFeatureItem).description,
                ) as SimplifiedFeatureItem[];
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 pb-[80px]">
                    {filtered.map(
                      (feature: SimplifiedFeatureItem, index: number) => {
                        return (
                          <div
                            key={index}
                            className={`rounded-xl bg-[linear-gradient(180deg,rgba(0,0,0,0.20)_0%,rgba(0,0,0,0)_100%)] shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)] backdrop-blur-[10px] p-6 w-full`}
                            style={{ transform: `translateY(${index * 24}px)` }}
                          >
                            <div className="text-white font-inter font-semibold text-[24px] leading-[140%]">
                              <HighlightedText
                                text={
                                  asText(
                                    (feature as SimplifiedFeatureItem)
                                      .description,
                                  ) || ''
                                }
                              />
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                );
              })()}
          </div>
        ) : isInterconnectedSteps ? (
          // Interconnected Steps variation: cards with connecting lines
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left side - Section Heading and Description */}
            <div className="lg:w-1/3 flex-shrink-0">
              {section_heading && (
                <h2 className="text-white font-inter font-semibold text-[26px] leading-[120%] mb-6">
                  <HighlightedText text={asText(section_heading) || ''} />
                </h2>
              )}
              {'leftsidedescription' in slice.primary &&
                slice.primary.leftsidedescription && (
                  <p className="text-white font-inter font-normal text-[18px] leading-[140%]">
                    {slice.primary.leftsidedescription}
                  </p>
                )}
            </div>

            {/* Right side - Feature Groups with Cards */}
            <div className="lg:w-2/3 flex flex-col gap-[30px]">
              {(slice.primary as InterconnectedStepsPrimary).features &&
                (slice.primary as InterconnectedStepsPrimary).features!.length >
                  0 && (
                  <>
                    {(() => {
                      const features =
                        (slice.primary as InterconnectedStepsPrimary)
                          .features ?? [];
                      let currentGroup: InterconnectedStepsFeatureItem[] = [];
                      const groups: InterconnectedStepsFeatureItem[][] = [];

                      // Group features by optional headline
                      features.forEach((feature, index) => {
                        const hasHeadline =
                          'optionalheadlinebeforecard' in feature &&
                          feature.optionalheadlinebeforecard;

                        if (hasHeadline && currentGroup.length > 0) {
                          groups.push([...currentGroup]);
                          currentGroup = [];
                        }
                        currentGroup.push(feature);

                        if (index === features.length - 1) {
                          groups.push([...currentGroup]);
                        }
                      });

                      return groups.map((group, groupIndex) => {
                        const firstFeature = group[0];
                        const hasHeadline =
                          'optionalheadlinebeforecard' in firstFeature &&
                          firstFeature.optionalheadlinebeforecard;

                        return (
                          <div key={groupIndex} className="flex flex-col">
                            {/* Optional headline */}
                            {hasHeadline && (
                              <h3 className="text-white font-inter font-semibold text-[26px] leading-[120%] mb-6">
                                {firstFeature.optionalheadlinebeforecard}
                              </h3>
                            )}

                            {/* Cards in this group */}
                            {group.map((feature, cardIndex) => {
                              const globalIndex =
                                groups
                                  .slice(0, groupIndex)
                                  .reduce((acc, g) => acc + g.length, 0) +
                                cardIndex;
                              const isLastInGroup =
                                cardIndex === group.length - 1;

                              return (
                                <div key={cardIndex}>
                                  <div
                                    ref={(el) => {
                                      cardsRef.current[globalIndex] = el;
                                    }}
                                    className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(0,0,0,0.2)] to-[rgba(0,0,0,0)] border border-[rgba(5,242,115,0.4)] rounded-2xl p-6 opacity-0 translate-y-4 transition-all duration-500 ease-out hover:scale-[1.02] hover:shadow-[0_8px_80px_0_rgba(0,0,0,0.15)]"
                                  >
                                    {feature.title && (
                                      <h4 className="text-[#05F273] font-inter font-bold text-[24px] leading-[140%] mb-2">
                                        <HighlightedText
                                          text={asText(feature.title) || ''}
                                        />
                                      </h4>
                                    )}
                                    {feature.description && (
                                      <div className="text-white font-inter font-normal text-[18px] leading-[140%]">
                                        <HighlightedText
                                          text={
                                            asText(feature.description) || ''
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* Connecting line - only if not last in group */}
                                  {!isLastInGroup && (
                                    <div className="flex items-center justify-center h-[30px] w-full">
                                      <svg
                                        width="2"
                                        height="30"
                                        viewBox="0 0 2 30"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <line
                                          x1="1"
                                          y1="0"
                                          x2="1"
                                          y2="30"
                                          stroke="#05F273"
                                          strokeWidth="2"
                                          strokeDasharray="4 4"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      });
                    })()}
                  </>
                )}
            </div>
          </div>
        ) : isDoubleCardSteps ? (
          // Double Card Steps variation: cards with sub-cards and connecting lines
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left side - Section Heading and Description */}
            <div className="lg:w-1/3 flex-shrink-0">
              {section_heading && (
                <h2 className="text-white font-inter font-medium text-[40px] leading-[120%] mb-6">
                  <HighlightedText text={asText(section_heading) || ''} />
                </h2>
              )}
              {'leftsidedescription' in slice.primary &&
                slice.primary.leftsidedescription && (
                  <div className="text-white font-inter font-normal text-[18px] leading-[150%]">
                    <p>{slice.primary.leftsidedescription}</p>
                  </div>
                )}
            </div>

            {/* Right side - Feature Cards with Sub-cards */}
            <div className="lg:w-[650px] flex flex-col gap-[30px]">
              {(slice.primary as DoubleCardStepsPrimary).features &&
                (slice.primary as DoubleCardStepsPrimary).features!.length >
                  0 && (
                  <>
                    {(() => {
                      const features =
                        (slice.primary as DoubleCardStepsPrimary).features ??
                        [];

                      return features.map(
                        (feature: DoubleCardStepsFeatureItem, index) => {
                          const isLastCard = index === features.length - 1;

                          return (
                            <div key={index} className="flex flex-col">
                              <div
                                ref={(el) => {
                                  cardsRef.current[index] = el;
                                }}
                                className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(0,0,0,0.2)] to-[rgba(0,0,0,0)] border border-[rgba(5,242,115,0.4)] rounded-2xl p-6 opacity-0 translate-y-4 transition-all duration-500 ease-out flex flex-col gap-2"
                              >
                                {/* Main Title */}
                                {feature.title && (
                                  <h4 className="text-[#05F273] font-inter font-bold text-[24px] leading-[1.4] mb-0">
                                    <HighlightedText
                                      text={asText(feature.title) || ''}
                                    />
                                  </h4>
                                )}

                                {/* Card 1 */}
                                {'card_1' in feature && feature.card_1 && (
                                  <div className="bg-[rgba(10,39,34,0.8)] rounded-3xl px-6 py-4 flex items-start gap-2">
                                    <div className="flex-shrink-0 w-10 h-10">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="40"
                                        height="40"
                                        viewBox="0 0 40 40"
                                        fill="none"
                                      >
                                        <path
                                          d="M19.9997 3.3335C10.8097 3.3335 3.33301 10.8102 3.33301 20.0002C3.33301 29.1902 10.8097 36.6668 19.9997 36.6668C29.1897 36.6668 36.6663 29.1902 36.6663 20.0002C36.6663 10.8102 29.1897 3.3335 19.9997 3.3335ZM19.9997 33.3335C12.648 33.3335 6.66634 27.3518 6.66634 20.0002C6.66634 12.6485 12.648 6.66683 19.9997 6.66683C27.3513 6.66683 33.333 12.6485 33.333 20.0002C33.333 27.3518 27.3513 33.3335 19.9997 33.3335Z"
                                          fill="#05F273"
                                        />
                                        <path
                                          d="M16.6647 22.6452L12.833 18.8202L10.4797 21.1802L16.6681 27.3552L27.8447 16.1785L25.4881 13.8219L16.6647 22.6452Z"
                                          fill="#05F273"
                                        />
                                      </svg>
                                    </div>
                                    <div className="flex-1 text-white font-inter font-normal text-[18px] leading-[1.4]">
                                      <HighlightedText
                                        text={asText(feature.card_1) || ''}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Card 2 */}
                                {'card_2' in feature && feature.card_2 && (
                                  <div className="bg-[rgba(10,39,34,0.8)] rounded-3xl px-6 py-4 flex items-start gap-2">
                                    <div className="flex-shrink-0 w-10 h-10">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="40"
                                        height="40"
                                        viewBox="0 0 40 40"
                                        fill="none"
                                      >
                                        <path
                                          d="M19.9997 3.3335C10.8097 3.3335 3.33301 10.8102 3.33301 20.0002C3.33301 29.1902 10.8097 36.6668 19.9997 36.6668C29.1897 36.6668 36.6663 29.1902 36.6663 20.0002C36.6663 10.8102 29.1897 3.3335 19.9997 3.3335ZM19.9997 33.3335C12.648 33.3335 6.66634 27.3518 6.66634 20.0002C6.66634 12.6485 12.648 6.66683 19.9997 6.66683C27.3513 6.66683 33.333 12.6485 33.333 20.0002C33.333 27.3518 27.3513 33.3335 19.9997 33.3335Z"
                                          fill="#05F273"
                                        />
                                        <path
                                          d="M16.6647 22.6452L12.833 18.8202L10.4797 21.1802L16.6681 27.3552L27.8447 16.1785L25.4881 13.8219L16.6647 22.6452Z"
                                          fill="#05F273"
                                        />
                                      </svg>
                                    </div>
                                    <div className="flex-1 text-white font-inter font-normal text-[18px] leading-[1.4]">
                                      <HighlightedText
                                        text={asText(feature.card_2) || ''}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Connecting line - only if not last card */}
                              {!isLastCard && (
                                <div className="flex items-center justify-center h-[30px] w-full">
                                  <svg
                                    width="2"
                                    height="30"
                                    viewBox="0 0 2 30"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <line
                                      x1="1"
                                      y1="0"
                                      x2="1"
                                      y2="30"
                                      stroke="#05F273"
                                      strokeWidth="2"
                                      strokeDasharray="4 4"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>
                          );
                        },
                      );
                    })()}
                  </>
                )}
            </div>
          </div>
        ) : isSimplified2Columns ? (
          // Simplified 2 Columns variation
          <div className="flex flex-col gap-[50px]">
            {/* Header Section */}
            <div className="flex flex-col gap-[10px]">
              {section_heading && (
                <h2 className="text-white font-inter font-medium text-[42px] leading-[120%]">
                  <HighlightedText text={asText(section_heading) || ''} />
                </h2>
              )}
              {'section_description' in slice.primary &&
                slice.primary.section_description && (
                  <p className="text-white font-inter font-normal text-[18px] leading-[150%]">
                    {slice.primary.section_description}
                  </p>
                )}
            </div>

            {/* Two Column Cards with Arrow */}
            {(slice.primary as Simplified2ColumnsPrimary).features &&
              (slice.primary as Simplified2ColumnsPrimary).features!.length >=
                2 && (
                <div className="flex gap-[26px] items-center">
                  {/* Left Card */}
                  <div className="flex-1">
                    {(() => {
                      const feature = (
                        slice.primary as Simplified2ColumnsPrimary
                      ).features![0] as Simplified2ColumnsFeatureItem;
                      return (
                        <div className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(0,0,0,0.20)] to-[rgba(0,0,0,0.00)] shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] rounded-[16px] p-[24px] flex flex-col gap-[24px]">
                          {feature.title && (
                            <div className="text-[#05F273] font-inter font-semibold text-[24px] leading-[140%]">
                              {feature.title}
                            </div>
                          )}
                          {feature.description && (
                            <div className="text-white font-inter font-normal text-[18px] leading-[140%]">
                              <HighlightedText
                                text={asText(feature.description) || ''}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Arrow Icon */}
                  <div className="flex items-center justify-center flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="45"
                      height="45"
                      viewBox="0 0 45 45"
                      fill="none"
                      className="rotate-180 scale-y-[-1]"
                    >
                      <path
                        d="M22.4641 32.8359L14.3891 24.7609C13.9688 24.3406 13.7344 23.7797 13.7344 23.1934C13.7344 22.607 13.9688 22.0461 14.3891 21.6258L22.4641 13.5508C22.8844 13.1305 23.4453 12.8961 24.0316 12.8961C24.618 12.8961 25.1789 13.1305 25.5992 13.5508C26.0195 13.9711 26.2539 14.532 26.2539 15.1184C26.2539 15.7047 26.0195 16.2656 25.5992 16.6859L20.8172 21.4688H31.3359C31.9223 21.4688 32.4832 21.7031 32.9035 22.1234C33.3238 22.5438 33.5581 23.1047 33.5581 23.691C33.5581 24.2773 33.3238 24.8383 32.9035 25.2586C32.4832 25.6789 31.9223 25.9133 31.3359 25.9133H20.8172L25.5992 30.6961C26.0195 31.1164 26.2539 31.6773 26.2539 32.2636C26.2539 32.85 26.0195 33.4109 25.5992 33.8312C25.1789 34.2515 24.618 34.4859 24.0316 34.4859C23.4453 34.4859 22.8844 34.2515 22.4641 33.8312V32.8359Z"
                        fill="#05F273"
                      />
                    </svg>
                  </div>

                  {/* Right Card with Border */}
                  <div className="flex-1">
                    {(() => {
                      const feature = (
                        slice.primary as Simplified2ColumnsPrimary
                      ).features![1] as Simplified2ColumnsFeatureItem;
                      return (
                        <div className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(0,0,0,0.20)] to-[rgba(0,0,0,0.00)] border border-[rgba(5,242,115,0.4)] rounded-[16px] p-[24px] flex flex-col gap-[8px] h-full">
                          {feature.title && (
                            <div className="text-[#05F273] font-inter font-bold text-[24px] leading-[140%]">
                              {feature.title}
                            </div>
                          )}
                          {feature.description && (
                            <div className="text-white font-inter font-normal text-[18px] leading-[140%]">
                              <HighlightedText
                                text={asText(feature.description) || ''}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left side - Section Heading */}
            <div className="lg:w-1/3 flex-shrink-0">
              {section_heading && (
                <h2 className="text-white font-inter font-medium text-[40px] leading-[120%]">
                  <HighlightedText text={asText(section_heading) || ''} />
                </h2>
              )}
            </div>

            {/* Right side - Features */}
            <div className="lg:w-2/3 flex flex-col gap-6">
              {(slice.primary as DefaultPrimary).features &&
                (slice.primary as DefaultPrimary).features!.length > 0 && (
                  <>
                    {((slice.primary as DefaultPrimary).features ?? []).map(
                      (feature: DefaultFeatureItem, index: number) => (
                        <div
                          key={index}
                          className="rounded-2xl bg-gradient-to-b from-[rgba(0,0,0,0.20)] to-[rgba(0,0,0,0.00)] shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)] backdrop-blur-[10px] p-6"
                        >
                          {feature.title && (
                            <h3 className="text-[#05F273] font-inter font-bold text-[24px] leading-[140%] mb-3">
                              <HighlightedText
                                text={asText(feature.title) || ''}
                              />
                            </h3>
                          )}
                          {feature.description && (
                            <div className="text-white font-inter font-normal text-[18px] leading-[140%]">
                              <HighlightedText
                                text={asText(feature.description) || ''}
                              />
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </>
                )}

              {/* Images - Single image with navigation */}
              {hasImages && (
                <div className="flex items-center justify-center mt-6">
                  {/* Left Arrow */}
                  {validImages.length > 1 && (
                    <button
                      onClick={prevImage}
                      className="flex-shrink-0 hover:opacity-80 transition-opacity mr-4"
                      aria-label="Previous image"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="46"
                        height="46"
                        viewBox="0 0 46 46"
                        fill="none"
                      >
                        <rect
                          width="46"
                          height="46"
                          transform="matrix(-1 0 0 1 46 0)"
                          fill="#0F423A"
                        />
                        <path
                          d="M21.697 13.9277C21.3889 13.6007 20.9711 13.417 20.5355 13.417C20.0999 13.417 19.6821 13.6007 19.374 13.9277L11.981 21.7776C11.673 22.1047 11.5 22.5483 11.5 23.0109C11.5 23.4734 11.673 23.917 11.981 24.2442L19.374 32.094C19.6838 32.4117 20.0988 32.5876 20.5296 32.5836C20.9603 32.5796 21.3724 32.3962 21.677 32.0727C21.9816 31.7493 22.1544 31.3118 22.1581 30.8544C22.1619 30.397 21.9963 29.9564 21.697 29.6274L17.2497 24.7553H32.8571C33.2928 24.7553 33.7107 24.5715 34.0188 24.2443C34.3269 23.9172 34.5 23.4735 34.5 23.0109C34.5 22.5482 34.3269 22.1045 34.0188 21.7774C33.7107 21.4502 33.2928 21.2664 32.8571 21.2664H17.2497L21.697 16.3943C22.005 16.0672 22.178 15.6236 22.178 15.161C22.178 14.6985 22.005 14.2549 21.697 13.9277Z"
                          fill="#002726"
                        />
                      </svg>
                    </button>
                  )}

                  {/* Single Image Display */}
                  <div
                    className="rounded-xl overflow-hidden"
                    style={{ width: '420px', height: '320px' }}
                  >
                    <PrismicNextImage
                      field={validImages[currentImageIndex].appview}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 60vw, 40vw"
                    />
                  </div>

                  {/* Right Arrow */}
                  {validImages.length > 1 && (
                    <button
                      onClick={nextImage}
                      className="flex-shrink-0 hover:opacity-80 transition-opacity ml-4"
                      aria-label="Next image"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="46"
                        height="46"
                        viewBox="0 0 46 46"
                        fill="none"
                      >
                        <rect width="46" height="46" fill="#0F423A" />
                        <path
                          d="M24.303 13.9277C24.6111 13.6007 25.0289 13.417 25.4645 13.417C25.9001 13.417 26.3179 13.6007 26.626 13.9277L34.019 21.7776C34.327 22.1047 34.5 22.5483 34.5 23.0109C34.5 23.4734 34.327 23.917 34.019 24.2442L26.626 32.094C26.3162 32.4117 25.9012 32.5876 25.4704 32.5836C25.0397 32.5796 24.6276 32.3962 24.323 32.0727C24.0184 31.7493 23.8456 31.3118 23.8419 30.8544C23.8381 30.397 24.0037 29.9564 24.303 29.6274L28.7503 24.7553H13.1429C12.7072 24.7553 12.2893 24.5715 11.9812 24.2443C11.6731 23.9172 11.5 23.4735 11.5 23.0109C11.5 22.5482 11.6731 22.1045 11.9812 21.7774C12.2893 21.4502 12.7072 21.2664 13.1429 21.2664H28.7503L24.303 16.3943C23.995 16.0672 23.822 15.6236 23.822 15.161C23.822 14.6985 23.995 14.2549 24.303 13.9277Z"
                          fill="#002726"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureHighlightsWithMedia;
